<template>
  <div class="docs-wrap">
    <!-- El shadow root cuelga de acá; su contenido lo maneja este componente
         con DOM plano (ver shadowMount.js). SIN overflow:hidden: la historia
         usa position:sticky y un ancestro que recorta la apaga. -->
    <div ref="host" class="docs-host"></div>
    <p v-if="error" class="docs-error">
      No pudimos cargar el detalle de Documentos PIE. Inicia sesión y prueba
      con tu próximo informe: los primeros son gratis.
    </p>
  </div>
</template>

<script>
// El cuerpo de /documentacion-pie/ dentro del chatbot demo: la
// historia del porqué (avanza con el scroll), lo que hace —cuatro campos de
// documentos llenándose solos—, la transferencia del Informe a la Familia al
// FU y el selector de los trece documentos.
//
// Mismo marcado y mismas hojas que la página (el endpoint renderiza el parcial
// marketing/_documentacion_secciones.html), montados en un shadow root. Los
// animadores de abajo son port de los scripts de la página, y los textos que
// teclean son parte de esos guiones: dos relojes, un solo contenido servido.
import informationService from "@/services/informationService";
import {
  montarEnSombra,
  saltosInternos,
  entradas,
  selector,
} from "./shadowMount";

// Lo que cambia respecto de la página. Va después de las hojas.
const AJUSTES =
  // Los márgenes de sección del sitio están pensados para una página entera.
  ".section{padding-block:1.75rem}" +
  ".shell{max-width:none;padding-inline:1.25rem}" +
  // Los "inicia sesión" del bloque: el menú del chat ya trae el suyo.
  ".btn-row{display:none!important}";

// --- los guiones de las animaciones (copiados del script de la página) -----
const T1 =
  "El progreso es notable en las áreas de lenguaje y comunicación, con un mejor desarrollo de la comprensión lectora y el manejo de conceptos matemáticos básicos, siempre con el apoyo de materiales concretos.";
const T2 =
  "Logra localizar información explícita en textos, identificando detalles y secuencias narrativas. En matemáticas, representa e identifica fracciones y realiza adiciones y sustracciones con igual denominador.";
const T3 =
  "Requiere monitoreo constante para mantener la atención en las actividades propuestas, además de trabajar en el respeto por las normas sociales y de aula.";
const H0 =
  "Leer y representar números hasta el 100 con material concreto, apoyada por la tabla posicional y verbalizando cada paso.";
const H2 =
  "Extraer información explícita de textos breves, respondiendo preguntas guiadas con apoyo de imágenes y organizadores gráficos.";
const AREAS = [
  {
    tab: "g3tA",
    sub: "Todas las subáreas",
    items: [
      ["Comprensión oral", "Formulación/selección de pregunta pertinente", "l"],
      ["Velocidad lectora", "Lectura en voz alta, palabras correctas por minuto (PCM)", "p"],
      ["Cálculo y numeración", "Comparar y ordenar números naturales hasta 1.000", "p"],
    ],
    par: "Formula preguntas pertinentes sobre lo escuchado y lee en voz alta con precisión, aunque bajo las palabras por minuto esperadas para 3° básico; compara y ordena números hasta 1.000 con apoyo.",
  },
  {
    tab: "g3tB",
    sub: "Ámbito socioemocional",
    items: [
      ["Autonomía y responsabilidad", "Completar primero tareas urgentes antes que tareas no urgentes", "p"],
      ["Compromiso y participación", "Hacer preguntas cuando no comprende", "l"],
      ["Expresión emocional y autorregulación", "Calmarse sin ayuda constante del adulto", "n"],
    ],
    par: "Requiere apoyo para priorizar tareas y organizar su trabajo. Participa en clases y pregunta cuando no comprende. En autorregulación emocional aún necesita la ayuda del adulto para calmarse frente a la frustración.",
  },
  {
    tab: "g3tC",
    sub: "Ámbito motor y sensorial",
    items: [
      ["Desarrollo motor y autocuidado", "Desplazarse por espacios escolares sin chocar frecuentemente", "l"],
      ["Desarrollo motor y autocuidado", "Mantener letra legible cuando escribe párrafos completos", "p"],
      ["Procesamiento sensorial auditivo", "Tolerar ruidos fuertes sin taparse los oídos", "n"],
    ],
    par: "En motricidad gruesa se desplaza con autonomía y coordinación acordes a su edad. En motricidad fina presenta un trazo irregular que enlentece la escritura; mejora con lápices triangulares. Tolera con dificultad los ruidos fuertes del aula.",
  },
];
const DX = { l: 12, p: 41, n: 70 };
const LV = [
  "Demostrar comprensión de las tablas de multiplicar hasta 10 utilizando representaciones concretas y pictóricas, expresando la multiplicación como adiciones y resolviendo problemas sencillos relacionados.",
  "Demostrar comprensión de las multiplicaciones hasta 5 utilizando representaciones concretas y pictóricas, expresándolas como adición de sumandos iguales y resolviendo problemas sencillos con apoyo.",
  "Representar multiplicaciones en el ámbito 0–5 como grupos de objetos iguales, usando materiales concretos, y resolver problemas simples con apoyo del adulto.",
  "Demostrar reconocimiento de grupos de objetos como multiplicación usando materiales concretos, con apoyo permanente.",
];
const CH = [
  [["del", "Elimina construir tablas de multiplicación"], ["keep", "Mantiene representación pictórica"], ["del", "Elimina la distributividad"]],
  [["red", "Reduce tablas hasta 10 → hasta 5"], ["del", "Elimina aplicar resultados sin calcular"], ["keep", "Mantiene problemas sencillos con apoyo"]],
  [["red", "Reduce a ámbito 0–5 con material concreto"], ["del", "Elimina la representación pictórica"], ["keep", "Mantiene problemas simples con apoyo"]],
  [["red", "Reduce ámbito 0–5 → 0–3"], ["red", "Simplifica el lenguaje"], ["keep", "Mantiene solo materiales concretos"], ["del", "Elimina resolver problemas"]],
];
const SYM = { del: "−", keep: "=", red: "↓" };
const TIPO = ["FU_Reevaluacion", "Informe_Familia_Final", "Informe_Psicopedagogico", "PAI", "PACI", "Anamnesis", "Estado_de_Avance", "PAEC", "FUDEI", "Valoracion_de_Salud"];
const NOMB = ["Emilia_Rojas", "Tomas_Perez", "Antonia_Diaz", "Benjamin_Soto", "Martina_Lopez", "Vicente_Munoz", "Sofia_Castro", "Matias_Reyes", "Isidora_Vega", "Lucas_Araya", "Florencia_Silva", "Agustin_Torres"];

export default {
  name: "DocumentacionSecciones",
  data() {
    return { error: false };
  },
  mounted() {
    informationService
      .getDocumentacionSecciones()
      .then((payload) => this.montar(payload))
      .catch(() => {
        this.error = true;
      });
  },
  beforeDestroy() {
    (this.limpiezas || []).forEach((fn) => fn());
  },
  methods: {
    montar(payload) {
      const montado = montarEnSombra(this.$refs.host, payload, {
        ajustes: AJUSTES,
      });
      if (!montado) {
        this.error = true;
        return;
      }
      this.raiz = montado.raiz;
      this.$id = (id) => this.raiz.getElementById(id);

      // `js` es la clase con la que las hojas apagan lo que las animaciones
      // encienden. Sin movimiento no se pone: todo queda quieto y completo.
      const conMovimiento =
        !!window.IntersectionObserver &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.limpiezas = [saltosInternos(this.raiz), selector(this.raiz, conMovimiento)];
      if (!conMovimiento) return;
      montado.cuerpo.classList.add("js");
      this.limpiezas.push(entradas(this.raiz));
      this.historia();
      this.animaciones();
    },

    // --- la historia del porqué, contada con el scroll ----------------------
    historia() {
      const $ = this.$id;
      const story = $("story");
      if (!story) return;
      const steps = Array.from(story.querySelectorAll(".story__step"));
      const FILES = [];
      for (let k = 0; k < 40; k++) {
        FILES.push(`${TIPO[(k * 3) % TIPO.length]}_${NOMB[(k * 7) % NOMB.length]}.docx`);
      }
      $("sflb").innerHTML = FILES.map(
        (f, k) =>
          `<div class="story__fr" id="sfr${k}"><i></i><span>${f}</span><em>${
            8 + (k % 20)
          } nov · ${38 + ((k * 7) % 60)} KB</em></div>`
      ).join("");

      let cur = -1;
      let timers = [];
      const at = (t, f) => timers.push(setTimeout(f, t));
      const fmt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const bump = (v) => {
        const n = $("snum");
        n.textContent = v;
        n.classList.add("bump");
        at(220, () => n.classList.remove("bump"));
      };
      const countFrom = (el, a, b, ms) => {
        const t0 = performance.now();
        const f = () => {
          const p = Math.min(1, (performance.now() - t0) / ms);
          const e = 1 - Math.pow(1 - p, 3);
          el.textContent = fmt(a + (b - a) * e);
          if (p < 1 && cur === 2) requestAnimationFrame(f);
        };
        f();
      };
      const setBeat = (i) => {
        if (i === cur) return;
        cur = i;
        story.setAttribute("data-beat", i);
        steps.forEach((s, k) => s.classList.toggle("on", k === i));
        timers.forEach(clearTimeout);
        timers = [];
        const num = $("snum");
        const lab = $("slab");
        if (i === 1) {
          num.textContent = "8";
          lab.textContent = "documentos por niño";
          lab.classList.remove("dark");
          at(900, () => bump("9"));
          at(1700, () => bump("10"));
        }
        if (i === 2) {
          num.textContent = "10";
          lab.textContent = "documentos al año";
          lab.classList.add("dark");
          const fb = $("sflb");
          const fw = $("sflw");
          fb.style.transform = "";
          $("sflc").textContent = "";
          FILES.forEach((f, k) => $(`sfr${k}`).classList.remove("on"));
          at(300, () => countFrom(num, 10, 1200, 2800));
          FILES.forEach((f, k) =>
            at(250 + k * 85, () => {
              const r = $(`sfr${k}`);
              r.classList.add("on");
              $("sflc").textContent = `${k + 1} archivos`;
              const rh = r.offsetHeight || 30;
              const vis = Math.max(3, Math.floor(fw.clientHeight / rh));
              if (k >= vis) fb.style.transform = `translateY(${-(k - vis + 1) * rh}px)`;
            })
          );
          at(250 + FILES.length * 85 + 300, () => {
            $("sflc").textContent = "1.200 archivos";
          });
        }
      };

      // El paso activo lo decide el scroll: en escritorio, el que cruza el
      // centro de la pantalla; en teléfono, el que asoma bajo el marco fijo.
      // Se escucha en captura sobre el documento para enterarse aunque quien
      // se desplace sea un contenedor del SPA y no la ventana.
      const stage = story.querySelector(".story__stage");
      let tick = false;
      const pick = () => {
        tick = false;
        const H = window.innerHeight;
        let line;
        if (window.matchMedia("(max-width:900px)").matches) {
          const fb = stage.getBoundingClientRect().bottom;
          line = fb + (H - fb) * 0.42;
        } else {
          line = H * 0.5;
        }
        let k = 0;
        steps.forEach((s, i) => {
          if (s.getBoundingClientRect().top < line) k = i;
        });
        setBeat(k);
      };
      const onScroll = () => {
        if (!tick) {
          tick = true;
          requestAnimationFrame(pick);
        }
      };
      document.addEventListener("scroll", onScroll, { passive: true, capture: true });
      window.addEventListener("resize", onScroll);
      pick();
      this.limpiezas.push(() => {
        document.removeEventListener("scroll", onScroll, { capture: true });
        window.removeEventListener("resize", onScroll);
        timers.forEach(clearTimeout);
      });
    },

    // --- las animaciones de producto -----------------------------------------
    // Un motor chico de tiempos + tecleo; cada animación en bucle mientras
    // está en pantalla, y la transferencia una sola vez con botón para repetir.
    animaciones() {
      const $ = this.$id;
      let JOBS = [];
      const tecleo = setInterval(() => {
        if (!JOBS.length) return;
        const now = performance.now();
        JOBS.forEach((j) => {
          const n = Math.min(j.s.length, Math.max(0, Math.floor((now - j.t) / j.d)));
          if (n !== j.n) {
            j.n = n;
            j.el.textContent = j.s.slice(0, n);
          }
        });
        JOBS = JOBS.filter((j) => j.n < j.s.length);
      }, 16);
      this.limpiezas.push(() => clearInterval(tecleo));

      const Loop = (rootId, build, total, once) => {
        const root = $(rootId);
        if (!root) return null;
        let timers = [];
        let running = false;
        const at = (t, fn) => timers.push(setTimeout(fn, t));
        const clear = () => {
          timers.forEach(clearTimeout);
          timers = [];
          JOBS = JOBS.filter((j) => !root.contains(j.el));
          root.querySelectorAll(".typing").forEach((e) => e.classList.remove("typing"));
        };
        const typ = (id, tx, t0, d) => {
          at(t0, () => {
            const el = $(id);
            JOBS = JOBS.filter((j) => j.el !== el);
            el.textContent = "";
            el.classList.add("typing");
            JOBS.push({ el, s: tx, t: performance.now(), d, n: 0 });
          });
          at(t0 + tx.length * d + 150, () => $(id).classList.remove("typing"));
        };
        const on = (id, c) => $(id).classList.add(c || "on");
        const off = (id, c) => $(id).classList.remove(c || "on");
        const cur = (cid, tid, dx, dy) => {
          const c = $(cid);
          const t = $(tid);
          if (!t) return;
          const r = t.getBoundingClientRect();
          const p = root.getBoundingClientRect();
          c.style.transform = `translate(${r.left - p.left + (dx || 0)}px,${
            r.top - p.top + (dy || 0)
          }px)`;
          c.classList.add("on");
        };
        const api = { at, typ, on, off, cur, $, root };
        const run = () => {
          clear();
          build(api);
          if (!once) at(total, run);
        };
        const io = new IntersectionObserver(
          (es) =>
            es.forEach((e) => {
              if (e.isIntersecting) {
                if (!running) {
                  running = true;
                  run();
                }
              } else if (!once) {
                running = false;
                clear();
              }
            }),
          { threshold: 0.3 }
        );
        io.observe(root);
        this.limpiezas.push(() => {
          io.disconnect();
          clear();
        });
        return { run };
      };

      // 01 · Sugerencias
      Loop("vg1", (a) => {
        ["g1q1", "g1q2"].forEach((i) => a.off(i, "lit"));
        ["g1l1", "g1l2"].forEach((i) => a.off(i, "hot"));
        a.off("g1s");
        ["g1a", "g1b", "g1c"].forEach((i) => a.off(i, "sel"));
        ["g1t1", "g1t2"].forEach((i) => {
          a.$(i).textContent = "";
          a.off(i, "ia");
        });
        a.off("g1d");
        a.off("g1cur");
        a.at(300, () => {
          a.on("g1q1", "lit");
          a.on("g1l1", "hot");
        });
        a.at(800, () => a.on("g1s"));
        a.at(1300, () => a.cur("g1cur", "g1b", 34, 10));
        a.at(1900, () => a.on("g1b", "sel"));
        a.at(2500, () => {
          a.off("g1s");
          a.off("g1cur");
          a.off("g1l1", "hot");
        });
        a.typ("g1t1", T1, 2700, 7);
        a.at(4300, () => {
          a.off("g1q1", "lit");
          a.on("g1q2", "lit");
          a.on("g1l2", "hot");
        });
        a.at(4800, () => a.cur("g1cur", "g1l2", 40, 8));
        a.at(5400, () => {
          a.on("g1t2", "ia");
          a.off("g1cur");
        });
        a.at(6300, () => a.off("g1t2", "ia"));
        a.typ("g1t2", T3, 6300, 6);
        a.at(7400, () => {
          a.off("g1q2", "lit");
          a.off("g1l2", "hot");
          a.on("g1d");
        });
      }, 9600);

      // 02 · Objetivos precargados
      Loop("vg2", (a) => {
        ["g2r0", "g2r1", "g2r2", "g2k0", "g2k1", "g2k2", "g2h0", "g2h1", "g2h2", "g2d", "g2cur"].forEach((i) =>
          a.off(i)
        );
        a.off("g2s2", "lit");
        a.$("g2s2").textContent = "Asignatura";
        ["g2h0", "g2h2"].forEach((i) => {
          a.$(i).textContent = "";
        });
        a.at(300, () => {
          a.on("g2s2", "lit");
          a.$("g2s2").textContent = "Todas las asignaturas";
        });
        a.at(800, () => a.on("g2r0"));
        a.at(1100, () => a.on("g2r1"));
        a.at(1400, () => a.on("g2r2"));
        a.at(2000, () => {
          a.off("g2s2", "lit");
          a.cur("g2cur", "g2k0", 12, 12);
        });
        a.at(2500, () => {
          a.on("g2k0");
          a.on("g2h0");
        });
        a.typ("g2h0", H0, 2600, 5);
        a.at(3900, () => a.cur("g2cur", "g2k2", 12, 12));
        a.at(4400, () => {
          a.on("g2k2");
          a.on("g2h2");
        });
        a.typ("g2h2", H2, 4500, 5);
        a.at(5800, () => {
          a.off("g2cur");
          a.on("g2d");
        });
      }, 8400);

      // 03 · Rúbricas del Informe Psicopedagógico
      Loop("vg3", (a) => {
        AREAS.forEach((A, i) => {
          const b = i * 5600;
          a.at(b, () => {
            ["g3tA", "g3tB", "g3tC"].forEach((t) => a.off(t, "lit"));
            a.on(A.tab, "lit");
            a.$("g3sub").textContent = A.sub;
            a.$("g3t").textContent = "";
            a.off("g3cur");
            A.items.forEach((it, k) => {
              a.$(`g3r${k}`).classList.remove("on", "l", "p", "n");
              a.$(`g3e${k}`).textContent = it[0];
              a.$(`g3x${k}`).textContent = it[1];
            });
          });
          A.items.forEach((it, k) => a.at(b + 350 + k * 250, () => a.on(`g3r${k}`)));
          A.items.forEach((it, k) => {
            const t = b + 1400 + k * 800;
            a.at(t, () => a.cur("g3cur", `g3a${k}`, DX[it[2]], 10));
            a.at(t + 420, () => a.on(`g3r${k}`, it[2]));
          });
          a.at(b + 3900, () => a.off("g3cur"));
          a.typ("g3t", A.par, b + 4000, 4);
        });
      }, 16800);

      // 04 · PACI con IA en cuatro niveles
      Loop("vg4", (a) => {
        const ch = a.$("g4ch");
        ch.innerHTML = '<span class="lab">Cambios:</span>';
        a.$("g4t").textContent = "";
        a.off("g4t", "ia");
        ["g4l0", "g4l1", "g4l2", "g4l3"].forEach((i) => a.off(i, "lit"));
        a.off("g4c0", "sel");
        a.off("g4ia");
        a.off("g4btn", "press");
        a.off("g4cur");
        a.at(300, () => a.on("g4c0", "sel"));
        a.at(700, () => a.on("g4ia"));
        a.at(1200, () => a.cur("g4cur", "g4btn", 60, 14));
        a.at(1600, () => a.on("g4btn", "press"));
        a.at(1900, () => {
          a.off("g4cur");
          a.on("g4t", "ia");
        });
        LV.forEach((tx, k) => {
          const b = 2600 + k * 2800;
          a.at(b, () => {
            ["g4l0", "g4l1", "g4l2", "g4l3"].forEach((i) => a.off(i, "lit"));
            a.on(`g4l${k}`, "lit");
            a.off("g4t", "ia");
            ch.innerHTML = '<span class="lab">Cambios:</span>';
          });
          a.typ("g4t", tx, b + 60, 4);
          a.at(b + 1000, () =>
            CH[k].forEach((c, j) => {
              const s = document.createElement("span");
              s.className = c[0];
              s.textContent = `${SYM[c[0]]} ${c[1]}`;
              ch.appendChild(s);
              a.at(40 + j * 140, () => s.classList.add("on"));
            })
          );
        });
      }, 14600);

      // Transferencia · el Informe a la Familia y el FU, una sola vez
      const vivo = Loop(
        "vivo",
        (a) => {
          a.root
            .querySelectorAll(".on,.lit,.hot,.sel,.fill")
            .forEach((e) => e.classList.remove("on", "lit", "hot", "sel", "fill"));
          ["va1", "va2", "va3", "vb1", "vb2", "vb3"].forEach((i) => {
            a.$(i).textContent = "";
          });
          a.$("vchat").classList.remove("gone");
          a.$("vcur").style.transform = "";
          a.at(400, () => a.on("vp1"));
          a.at(900, () => a.on("vp2"));
          a.at(1400, () => a.on("vp3"));
          a.at(2000, () => a.on("vm4"));
          a.at(3400, () => {
            a.on("vchat", "gone");
            a.on("vsplit");
          });
          a.at(3500, () => a.on("vd1"));
          a.at(3800, () => a.on("vd2"));
          a.at(4300, () => {
            a.on("vq1", "lit");
            a.on("vl1", "hot");
          });
          a.at(4900, () => a.on("vs1"));
          a.at(5200, () => a.cur("vcur", "vs1b", 40, 10));
          a.at(6000, () => a.on("vs1b", "sel"));
          a.at(6500, () => {
            a.off("vs1");
            a.off("vl1", "hot");
            a.off("vcur");
          });
          a.typ("va1", T1, 6700, 9);
          a.at(6700, () => a.off("vq1", "lit"));
          a.at(8700, () => a.on("vq2", "lit"));
          a.typ("va2", T2, 8800, 5);
          a.at(9900, () => {
            a.off("vq2", "lit");
            a.on("vq3", "lit");
          });
          a.typ("va3", T3, 10000, 5);
          a.at(10900, () => a.off("vq3", "lit"));
          a.at(11300, () => {
            a.on("vdn1");
            a.on("vw1");
          });
          a.at(12300, () => a.on("varrow"));
          a.at(12800, () => a.on("vb1", "fill"));
          a.typ("vb1", T1, 12800, 2);
          a.at(13300, () => a.on("vb2", "fill"));
          a.typ("vb2", T2, 13300, 2);
          a.at(13800, () => a.on("vb3", "fill"));
          a.typ("vb3", T3, 13800, 2);
          a.at(14600, () => {
            a.on("vdn2");
            a.off("varrow");
          });
          a.at(15200, () => a.on("vagain"));
        },
        15400,
        true
      );
      if (vivo) $("vagain").addEventListener("click", vivo.run);
    },
  },
};
</script>

<style scoped>
.docs-wrap {
  width: 100%;
}

/* Sin overflow:hidden a propósito: recortar acá apaga el position:sticky de
   la historia. Las esquinas redondeadas las pone el contenido. */
.docs-host {
  display: block;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 46px rgba(57, 62, 70, 0.15);
}

.docs-error {
  margin: 0;
  padding: 1.2rem 1.4rem;
  color: #5c636d;
  text-align: left;
}
</style>
